import { createStore } from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import CryptoJS from 'crypto-js';
import axios from 'axios';

// Configuración para roles
const ROLE_STORAGE_KEY = 'user_role';
// const CACHE_EXPIRATION_MS = 3 * 1000; // 30 segundos para pruebas
const CACHE_EXPIRATION_MS = 15 * 60 * 1000; // 30 minutos para producción
// const CACHE_EXPIRATION_MS = 24 * 60 * 60 * 1000; // 1 dia para producción
const SECRET_KEY = 's3cr3tK3y!@#2025'; // Clave secreta segura
let roleFetchPromise = null
let roleRefreshIntervalId = null

function encrypt(data) {
  return CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
}

function decrypt(data) {
  try {
    const bytes = CryptoJS.AES.decrypt(data, SECRET_KEY);
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  } catch (_error) {
    return null;
  }
}

export default createStore({
  state: {
    isLoggedIn: false,
    isFirstLoad: true,
    user: null,
    isAuthenticated: false,
    roleToken: '', // Default role token
    role: null, // Rol actual del usuario
    roleLoaded: false, // Indicador si el rol está cargado
    tokens: {
      clavePendiente: null,
      tokenFinal: null,
    },
  },
  mutations: {
    setSelectedCameraId(state, deviceId) {
      state.selectedCameraId = deviceId;
    },
    setRole(state, role) {
      state.role = role;
      state.roleLoaded = true;
    },
    setTokens(state, tokens) {
      state.tokens = tokens;
    },
    clearState(state) {
      state.tokens = {
        clavePendiente: null,
        tokenFinal: null,
      };
      state.isFirstLoad = true;
    },
    setLoginState(state, isLoggedIn) {
      state.isLoggedIn = isLoggedIn;
    },
    setUser(state, userData) {
      state.user = userData;
      state.isAuthenticated = true;
      state.isLoggedIn = true;
      state.roleToken = userData.role; // Guardar el token del rol encriptado
    },
    clearUser(state) {
      state.user = null;
      state.isAuthenticated = false;
      state.isLoggedIn = false;
      state.roleToken = ''; // Reset role token
      state.role = null; // Limpia el rol del usuario
      state.roleLoaded = false;
    },
    setRoleToken(state, roleToken) {
      state.roleToken = roleToken;
    },
    clearRole(state) {
      state.role = null;
      state.roleLoaded = false;
      state.roleToken = '';
    },
  },
  actions: {
    async login({ commit, dispatch }, user) {
      commit('setUser', user)
      commit('setLoginState', true)

      // ✅ al loguear, valida rol una vez (y arranca refresco)
      await dispatch('fetchRole', { force: true })
      dispatch('startRoleAutoRefresh')
    },

    logout({ commit }) {
      commit('clearUser')
      commit('setLoginState', false)
      localStorage.removeItem(ROLE_STORAGE_KEY)

      // ✅ evita múltiples intervals vivos
      if (roleRefreshIntervalId) {
        clearInterval(roleRefreshIntervalId)
        roleRefreshIntervalId = null
      }
    },

    async fetchRole({ commit, state }, { force = false } = {}) {
      // Si no hay sesión, marca "cargado" y limpia
      if (!state.roleToken || !state.user?.token) {
        commit('setRole', null) // roleLoaded=true (tu mutation setRole ya lo hace)
        return null
      }

      // ✅ Cache local (15 min)
      if (!force) {
        const cachedRole = localStorage.getItem(ROLE_STORAGE_KEY)
        if (cachedRole) {
          const parsed = decrypt(cachedRole)
          if (parsed?.role && parsed?.timestamp && (Date.now() - parsed.timestamp < CACHE_EXPIRATION_MS)) {
            commit('setRole', parsed.role)
            return parsed.role
          }
        }
      }

      // ✅ Dedup: si ya hay una petición en vuelo, reúsala
      if (roleFetchPromise) return roleFetchPromise

      roleFetchPromise = (async () => {
        try {
          const response = await axios.post('/api/verify-role', {
            roleToken: state.roleToken,
            userToken: state.user?.token
          })

          const { role, newRoleToken } = response.data || {}

          if (!role) throw new Error('Rol inválido devuelto por el servidor')

          // cachea rol + timestamp (15 min)
          localStorage.setItem(
            ROLE_STORAGE_KEY,
            encrypt({ role, timestamp: Date.now() })
          )

          commit('setRole', role)

          // ✅ newRoleToken opcional
          if (newRoleToken) {
            commit('setRoleToken', newRoleToken)
            // actualiza el user persistido (si lo usas para mantener roleToken)
            commit('setUser', { ...state.user, role: newRoleToken })
          }

          return role
        } catch (_error) {
          commit('setRole', null)
          return null
        } finally {
          roleFetchPromise = null
        }
      })()

      return roleFetchPromise
    },

    startRoleAutoRefresh({ dispatch, state }) {
      // ✅ evita interval duplicado
      if (roleRefreshIntervalId) {
        clearInterval(roleRefreshIntervalId)
        roleRefreshIntervalId = null
      }

      // si no hay sesión, no hagas nada
      if (!state.roleToken || !state.user?.token) return

      roleRefreshIntervalId = setInterval(() => {
        // solo refresca si sigue logueado
        if (state.roleToken && state.user?.token) {
          dispatch('fetchRole', { force: true })
        }
      }, CACHE_EXPIRATION_MS)
    }
  },

  plugins: [
    createPersistedState({
      paths: [
        'tokens',
        'isLoggedIn',
        'user',
        'isAuthenticated',
        'roleToken',
      ],
    }),
  ],
});
